import {
  gddRangeToMsg,
  mixedKeysToMsg,
  calculateGdd,
  baskervilleEmin,
} from "../../utils/helpers"
import { format } from "date-fns"

export default function AppleMaggotLogic(
  modelData,
  data,
  dateOfInterest,
  isModelInSeason,
  userBiofix1 = null, // String 2021-07-23
  userPestStage = null
) {
  // console.count("ModelLogic------------------------")
  const { baseFormula, elements, baseFormulaDisplay, base } = modelData
  const { userInputs } = elements
  const { beforeBiofix1, afterBiofix1 } = elements.managementGuide

  const dailyDataWithGdd = calculateGdd(
    data.dailyData,
    base,
    0,
    baskervilleEmin
  ).map((d) => {
    return { ...d, dateDisplay: format(d.date, "yyyy-MM-dd") }
  })
  // console.log(userBiofix1, userBiofix2)

  // User Biofix dates -------------------------------------------------------
  let userBiofix1Index = 0
  if (userBiofix1 !== null) {
    userBiofix1Index = dailyDataWithGdd.findIndex(
      (day) => day.dateDisplay === userBiofix1
    )
  }

  // Recalculating degree-days when biofix1 ----------------------------------
  let gdd1 = 0
  let indices = []
  const mData = dailyDataWithGdd.map((day, i) => {
    let p = {
      date: day.date,
      dateDisplay: day.dateDisplay,
      ms: day.ms,
      dayOfYear: day.dayOfYear,
      isForecast: day.isForecast,
      [`${baseFormula}dd`]: day.dd,
      [`${baseFormula}Gdd`]: day.gdd,
      displayBiofix1Input:
        day.gdd >= userInputs.firstInput.displayElement ? true : false,
      biofix1Gdd: "N/A",
    }

    // Recalculate if user has selected a biofix1 date
    if (userBiofix1 !== null) {
      if (i >= userBiofix1Index) {
        indices.push(i)
        gdd1 += day.dd !== "N/A" ? day.dd : 0
        p.biofix1Gdd = Math.round(gdd1)
      }
    } else {
      if (day.gdd >= userInputs.firstInput.biofix1) {
        indices.push(i)
        gdd1 += day.dd !== "N/A" ? day.dd : 0
        p.biofix1Gdd = Math.round(gdd1)
      }
    }
    return p
  })
  // console.log(mData)
  ////////////////////////////////////////////////////////////////////////////////////
  let selectedDate = mData.find(
    (day) => day.dayOfYear === dateOfInterest.dayOfYear
  )

  if (!selectedDate) {
    selectedDate = mData.find((d) => d.isForecast)
  }
  // Biofix Dates ----------------------------------------------------------
  let biofix1 = null
  if (indices.length !== 0) {
    biofix1 = mData[indices[0]]
  }

  if (biofix1) {
    if (selectedDate.dayOfYear < biofix1.dayOfYear) biofix1 = null
  }
  // console.log(biofix1)
  ////////////////////////////////////////////////////////////////////////////////////

  // determine biofix message Below user inputs ------------------------------------
  let userInputMsg = ""
  if (isModelInSeason) {
    if (biofix1 === null) {
      userInputMsg = userInputs.firstInput.message.beforeBiofix1
    }
    if (biofix1) {
      userInputMsg = userInputs.firstInput.message.afterBiofix1
    }
  }
  ////////////////////////////////////////////////////////////////////////////////////

  // User input DD Accumulation ------------------------------------------------------
  let startDate = data.dailyData[0].date.toLocaleDateString()
  let gddDisplay = selectedDate[`${baseFormula}Gdd`]

  if (biofix1) {
    startDate = "first trap catch"
    gddDisplay = selectedDate.biofix1Gdd
  }

  const accumulationDDMsg = {
    text: `Accumulated degree days ${baseFormulaDisplay} ${startDate} through ${data.dailyData[
      selectedDate.dayOfYear - 1
    ].date.toLocaleDateString()}:`,
    gdd: gddDisplay,
  }
  // console.log(accumulationDDMsg)
  // /////////////////////////////////////////////////////////////////////////////////////

  // Data to display in Results Table --------------------------------------------------
  let tableData = [...mData]
  if (mData.length >= 8) {
    tableData = mData.slice(
      selectedDate.dayOfYear - 3,
      selectedDate.dayOfYear + 5
    )
  }
  // console.log(tableData)
  // //////////////////////////////////////////////////////////////////////////////////////

  // Data in CSV format to export ------------------------------------------------------
  let csvData = []
  if (isModelInSeason) {
    csvData = mData.map((day) => {
      if (biofix1) {
        return {
          Date: day.date,
          [`Daily DD ${baseFormulaDisplay}`]: day[`${baseFormula}dd`],
          "From Jan 1": day[`${baseFormula}Gdd`],
          [`From ${format(biofix1.date, "LLL d")}`]: day.biofix1Gdd,
        }
      }
      return {
        Date: day.date,
        [`Daily DD ${baseFormulaDisplay}`]: day[`${baseFormula}dd`],
        "From Jan 1": day[`${baseFormula}Gdd`],
      }
    })
  }
  // console.log(csvData)
  //////////////////////////////////////////////////////////////////////////////////////

  // Management Guide ------------------------------------------------------------------
  let managementGuide = {
    pestStage: "",
    pestStatus: "",
    pestManagement: "",
    dashboardDisplay: "",
  }

  const msg = gddRangeToMsg(beforeBiofix1, selectedDate[`${baseFormula}Gdd`])
  if (msg && msg !== "N/A") managementGuide = msg

  if (biofix1) {
    const msg = mixedKeysToMsg(afterBiofix1, biofix1, selectedDate)
    if (msg) managementGuide = msg
  }

  // console.log(managementGuide)
  //////////////////////////////////////////////////////////////////////////////////////

  // Determine Phenological Stage ----------------------------------------------------
  let phenologicalStage = null
  //////////////////////////////////////////////////////////////////////////////////////

  // Determine Pest Stage -----------------------------------------------------------
  let pestStage = null

  if (biofix1) {
    if (userPestStage) {
      pestStage = { ...userPestStage }
      managementGuide = elements.managementGuide.afterBiofix1[userPestStage.key]
    } else {
      const res = mixedKeysToMsg(afterBiofix1, biofix1, selectedDate)
      if (res) {
        pestStage = { ...res, value: res.pestStage }
        managementGuide = { ...res }
      }
    }
  }

  // console.log(pestStage)
  //////////////////////////////////////////////////////////////////////////////////////

  if (!isModelInSeason) {
    managementGuide = { ...elements.managementGuide.outOfSeasonMsg }
  }

  // Dashboard Summary -----------------------------------------------------------------
  let dashboardSummary = {
    message: "",
    fromJan1: {
      text: `Accum. DD ${baseFormulaDisplay} since Jan 1:`,
      gdd: "N/A",
    },
    [biofix1 ? "biofix1" : null]: {
      text: `Accum. DD ${baseFormulaDisplay} from first trap catch:`,
      gdd: "N/A",
    },
  }

  if (isModelInSeason === false) {
    dashboardSummary.message =
      elements.managementGuide.outOfSeasonMsg.dashboardDisplay
  } else {
    const lastDate = mData.find((d) => d.isForecast)
    if (lastDate) {
      dashboardSummary.message = managementGuide.dashboardDisplay
      dashboardSummary.fromJan1.gdd = lastDate[`${baseFormula}Gdd`]
      if (biofix1) {
        dashboardSummary.biofix1.gdd = lastDate.biofix1Gdd
      }
    }
  }

  // console.log(dashboardSummary)
  //////////////////////////////////////////////////////////////////////////////////////

  return {
    data: mData,
    biofix1,
    selectedDate,
    userInputMsg,
    accumulationDDMsg,
    tableData,
    csvData,
    managementGuide,
    phenologicalStage,
    pestStage,
    dashboardSummary,
  }
}
